import {Platform} from 'react-native';
import {finixApplicationId, finixEnvironment} from '../constants';

/**
 * Finix card tokenization for the withdrawal destination card (SMG-4557).
 *
 * <p><b>Why this is not shaped like cardTokenization.ts.</b> The eProtect flow next door takes a
 * PAN we already hold and posts it for a token. Finix refuses to work that way: `window.Finix`
 * only exposes form constructors, and the tokenization request is made from inside Finix's own
 * cross-origin iframe. There is no entry point that accepts card data we collected ourselves.
 * That is the point of it — the PAN never enters our origin — but it means Finix must own the
 * inputs, so this module mounts a form rather than exposing a `tokenize(number, cvc)`.</p>
 *
 * <p>Consequence worth stating plainly: a card entered through this form is tokenized with Finix
 * and <b>not</b> with WorldPay, because only one provider can own the fields. A Finix-entered
 * card is therefore a Finix payout destination; the WorldPay tier would decline it cleanly
 * ("requires a stored card token; user has none on file") and the cascade moves on.</p>
 */

type FinixTokenizationErrorCode =
  | 'UNSUPPORTED_PLATFORM' | 'CONFIGURATION_ERROR' | 'SDK_LOAD_FAILED' | 'SDK_LOAD_TIMEOUT'
  | 'SDK_UNAVAILABLE' | 'FORM_NOT_MOUNTED' | 'TOKENIZATION_FAILED' | 'TOKENIZATION_TIMEOUT';

export type FinixTokenResult = {token: string};

type FinixSubmitResponse = {data?: {id?: string}};
export type FinixForm = {
  submit: (
    environment: string,
    applicationId: string,
    callback: (error: unknown, response: FinixSubmitResponse) => void
  ) => void
};
type FinixGlobal = {
  CardTokenForm?: (elementId: string, options: Record<string, unknown>) => FinixForm
};
type FinixWindow = Window & {Finix?: FinixGlobal};

const sdkScriptId = 'evenplay-finix-sdk';
const sdkSrc = 'https://js.finix.com/v/1/finix.js';
const sdkLoadTimeoutMs = 15000;
const tokenizationTimeoutMs = 15000;
const tokenizationSafetyTimeoutMs = tokenizationTimeoutMs + 5000;

export const finixTokenizationPreparationErrorMessage =
  'Secure card entry could not be prepared. Please try again.';

const finixTokenizationFailedMessage =
  'Unable to verify this card. Please check the details and try again.';

const isFinixTokenizationSupported = () => Platform.OS === 'web';

let sdkLoadPromise: Promise<void> | undefined;

class FinixTokenizationError extends Error {
  constructor(public readonly code: FinixTokenizationErrorCode, message: string) {
    super(message);
    this.name = 'FinixTokenizationError';
  }
}

const preparationErrorCodes = new Set<FinixTokenizationErrorCode>([
  'UNSUPPORTED_PLATFORM', 'CONFIGURATION_ERROR', 'SDK_LOAD_FAILED', 'SDK_LOAD_TIMEOUT',
  'SDK_UNAVAILABLE', 'FORM_NOT_MOUNTED'
]);

const getErrorCode = (error: unknown): FinixTokenizationErrorCode | undefined => {
  const code = (error as {code?: unknown})?.code;
  return typeof code === 'string' ? code as FinixTokenizationErrorCode : undefined;
};

export const isFinixTokenizationPreparationError = (error: unknown) => {
  const code = getErrorCode(error);
  return !!code && preparationErrorCodes.has(code);
};

/** Code only — a tokenization failure message can echo card data back. */
export const buildSafeFinixTokenizationErrorLog = (error: unknown) => ({code: getErrorCode(error) || 'UNKNOWN'});

const getConfig = () => {
  const applicationId = typeof finixApplicationId === 'string' ? finixApplicationId.trim() : '';
  const environment = typeof finixEnvironment === 'string' ? finixEnvironment.trim() : '';

  // Finix rejects an application id belonging to the other environment, so both must be present
  // and deliberate rather than defaulted.
  if (!applicationId || (environment !== 'sandbox' && environment !== 'live')) {
    throw new FinixTokenizationError('CONFIGURATION_ERROR', finixTokenizationPreparationErrorMessage);
  }
  return {applicationId, environment};
};

/** Can this build offer Finix card entry at all? Drives which entry UI the withdraw flow shows. */
export const isFinixConfigured = () => {
  if (!isFinixTokenizationSupported()) return false;
  try {
    getConfig();
    return true;
  } catch {
    return false;
  }
};

const getFinix = () => {
  const finix = (window as FinixWindow).Finix;
  return typeof finix?.CardTokenForm === 'function' ? finix : undefined;
};

const prepareFinixTokenization = (): Promise<void> => {
  if (!isFinixTokenizationSupported() || typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new FinixTokenizationError('UNSUPPORTED_PLATFORM', 'Card entry is not available on this platform yet.'));
  }
  try {
    getConfig();
  } catch (error) {
    return Promise.reject(error);
  }

  if (getFinix()) return Promise.resolve();
  if (sdkLoadPromise) return sdkLoadPromise;

  const pending = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(sdkScriptId) as HTMLScriptElement | null;
    const script = existingScript || document.createElement('script');
    let settled = false;

    const cleanup = () => {
      clearTimeout(timeout);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
    const fail = (error: FinixTokenizationError) => {
      if (settled) return;
      settled = true;
      cleanup();
      script.remove();
      reject(error);
    };
    const handleLoad = () => {
      if (settled) return;
      if (!getFinix()) {
        fail(new FinixTokenizationError('SDK_UNAVAILABLE', finixTokenizationPreparationErrorMessage));
        return;
      }
      settled = true;
      cleanup();
      resolve();
    };
    const handleError = () => fail(new FinixTokenizationError('SDK_LOAD_FAILED', finixTokenizationPreparationErrorMessage));
    const timeout = setTimeout(
      () => fail(new FinixTokenizationError('SDK_LOAD_TIMEOUT', finixTokenizationPreparationErrorMessage)),
      sdkLoadTimeoutMs
    );

    script.addEventListener('load', handleLoad, {once: true});
    script.addEventListener('error', handleError, {once: true});

    if (!existingScript) {
      script.id = sdkScriptId;
      script.async = true;
      script.src = sdkSrc;
      document.head.appendChild(script);
    }
  });

  sdkLoadPromise = pending;
  void pending.catch(() => {
    if (sdkLoadPromise === pending) sdkLoadPromise = undefined;
  });
  return pending;
};


/**
 * Mount Finix's hosted card fields into an existing element, returning the form handle to submit
 * later plus an unmount. The element must already be in the DOM — Finix resolves it by id.
 */
export const mountFinixCardForm = async (elementId: string, options: Record<string, unknown> = {}) => {
  await prepareFinixTokenization();
  const finix = getFinix();
  if (!finix?.CardTokenForm) {
    throw new FinixTokenizationError('SDK_UNAVAILABLE', finixTokenizationPreparationErrorMessage);
  }
  if (typeof document === 'undefined' || !document.getElementById(elementId)) {
    throw new FinixTokenizationError('FORM_NOT_MOUNTED', finixTokenizationPreparationErrorMessage);
  }

  const form = finix.CardTokenForm(elementId, options);
  return {
    form,
    unmount: () => {
      const host = document.getElementById(elementId);
      // replaceChildren(), not innerHTML: this detaches Finix's iframe, it does not render markup.
      if (host) host.replaceChildren();
    }
  };
};

/**
 * Submit a mounted form and resolve the resulting `TK…` token.
 *
 * <p>Finix's callback fires once in the happy path but is not contractually single-shot, and it
 * can fail to fire at all if the iframe is torn down mid-flight — hence the settled guard and the
 * safety timeout, matching the eProtect wrapper next door.</p>
 */
export const submitFinixCardForm = (form: FinixForm): Promise<FinixTokenResult> => {
  const {applicationId, environment} = getConfig();

  return new Promise<FinixTokenResult>((resolve, reject) => {
    let settled = false;
    let safetyTimeout: ReturnType<typeof setTimeout>;

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(safetyTimeout);
      callback();
    };
    const failTokenization = () => finish(() => reject(
      new FinixTokenizationError('TOKENIZATION_FAILED', finixTokenizationFailedMessage)
    ));

    safetyTimeout = setTimeout(
      () => finish(() => reject(new FinixTokenizationError(
        'TOKENIZATION_TIMEOUT',
        'We are experiencing technical difficulties. Please try again later or contact support. (timeout)'
      ))),
      tokenizationSafetyTimeoutMs
    );

    try {
      form.submit(environment, applicationId, (error, response) => {
        const token = response?.data?.id;
        if (error || !token) {
          failTokenization();
          return;
        }
        finish(() => resolve({token}));
      });
    } catch {
      failTokenization();
    }
  });
};
